import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';
import apolloClient from '../../graphql/apollo-client';
import { GET_CAMPAIGN } from '../../graphql/queries';
import { graphqlApi } from '../../services/graphql';
import { Spinner } from '../common/Spinner';
import ChannelIcon from './ChannelIcon';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

type TabType = 'applicants' | 'progress' | 'settlement';

interface EnrichedApplication {
  matchId: string;
  campaignId: string;
  influencerId: string;
  userId: string;
  status: string;
  appliedAt: string;
  createdAt: string;
  coverLetter?: string;
  portfolioItems?: string[];
  socialMediaStats?: any;
  influencerName?: string;
  nickname?: string;
  profileImage?: string;
  socialMediaHandles?: Record<string, { account?: string; followerCount?: number }>;
  rewardAmount?: number;
}

const CampaignApplicantsPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['campaigns', 'common', 'profile']);
  const [campaign, setCampaign] = useState<any>(null);
  const [applications, setApplications] = useState<EnrichedApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('applicants');
  const [channelFilter, setChannelFilter] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('recent');
  const [progressFilter, setProgressFilter] = useState<'all' | 'in_progress' | 'settlement_needed' | 'done' | 'canceled'>('all');
  const [selectedApp, setSelectedApp] = useState<EnrichedApplication | null>(null);
  const [selectedMatchDetail, setSelectedMatchDetail] = useState<any>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'applied' | 'shortlisted' | 'accepted'>('all');
  const [contentReviewApp, setContentReviewApp] = useState<EnrichedApplication | null>(null);
  const [contentReviewMatch, setContentReviewMatch] = useState<any>(null);
  const [contentReviewLoading, setContentReviewLoading] = useState(false);
  const [cancelModalApp, setCancelModalApp] = useState<EnrichedApplication | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [reviewModalApp, setReviewModalApp] = useState<EnrichedApplication | null>(null);
  const [reviewGrades, setReviewGrades] = useState({ communicationSpeed: 0, visitPunctuality: 0, collaborationAttitude: 0, submissionPunctuality: 0, contentQuality: 0 });
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const [campRes, appRes] = await Promise.all([
        apolloClient.query({ query: gql`${GET_CAMPAIGN}`, variables: { campaignId }, fetchPolicy: 'network-only' }),
        graphqlApi.getMatches({ campaignId } as any),
      ]);
      setCampaign(campRes.data?.getCampaign || null);

      const rawMatches: any[] = appRes.success && appRes.data ? (appRes.data.matches || []) : [];

      // Bulk fetch all influencer profiles in one request
      const uniqueInfluencerIds = Array.from(new Set(rawMatches.map((m: any) => m.influencerId).filter(Boolean)));
      let influencerMap: Record<string, any> = {};
      if (uniqueInfluencerIds.length > 0) {
        try {
          const infRes = await graphqlApi.getInfluencersByIds(uniqueInfluencerIds);
          if (infRes.success && infRes.data) {
            for (const inf of infRes.data) {
              influencerMap[inf.influencerId] = inf;
            }
          }
        } catch { /* ignore */ }
      }

      const enriched: EnrichedApplication[] = rawMatches.map((m: any) => {
        const inf = influencerMap[m.influencerId];
        return {
          ...m,
          rewardAmount: m.rewardAmount,
          influencerName: inf?.name || inf?.nickname,
          nickname: inf?.nickname || inf?.name,
          profileImage: inf?.profileImage,
          socialMediaHandles: inf?.socialMediaHandles,
        };
      });

      setApplications(enriched);
    } catch (err) { console.error('Failed to fetch:', err); }
    finally { setLoading(false); }
  }, [campaignId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openProfileModal = async (app: EnrichedApplication) => {
    setSelectedApp(app);
    setModalLoading(true);
    try {
      const res = await graphqlApi.getMatch(app.matchId);
      if (res.success && res.data) setSelectedMatchDetail(res.data);
    } catch { /* ignore */ }
    finally { setModalLoading(false); }
  };

  const closeProfileModal = () => { setSelectedApp(null); setSelectedMatchDetail(null); };

  const openContentReviewModal = async (app: EnrichedApplication) => {
    setContentReviewApp(app);
    setContentReviewLoading(true);
    try {
      const res = await graphqlApi.getMatch(app.matchId);
      if (res.success && res.data) setContentReviewMatch(res.data);
    } catch { /* ignore */ }
    finally { setContentReviewLoading(false); }
  };
  const closeContentReviewModal = () => { setContentReviewApp(null); setContentReviewMatch(null); };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Spinner /></div>;
  if (!campaign) return <div className="max-w-6xl mx-auto px-4 py-6 text-center text-gray-400">{t('applicants.notFound')}</div>;

  const channels: string[] = campaign.contentChannels?.length > 0
    ? campaign.contentChannels
    : campaign.contentChannel ? [campaign.contentChannel] : [];

  const applicantCount = applications.length;
  const firstRoundCount = applications.filter((a: any) => a.status === 'shortlisted' || a.status === 'requested').length;
  const finalCount = applications.filter((a: any) => a.status === 'accepted' || a.status === 'completed').length;

  // Filter and sort
  let filtered = [...applications];
  // Status filter
  if (statusFilter === 'applied') {
    filtered = filtered.filter(a => a.status === 'applied');
  } else if (statusFilter === 'shortlisted') {
    filtered = filtered.filter(a => a.status === 'shortlisted' || a.status === 'requested');
  } else if (statusFilter === 'accepted') {
    filtered = filtered.filter(a => a.status === 'accepted');
  }
  // all: show all statuses
  if (statusFilter === 'all') {
    filtered = filtered.filter(a => ['applied', 'shortlisted', 'requested', 'accepted', 'completed', 'canceled'].includes(a.status));
  }
  if (channelFilter.length > 0) {
    filtered = filtered.filter((a) => {
      const handles = a.socialMediaHandles || {};
      // Map filter button keys to socialMediaHandles keys
      const channelToHandleKey: Record<string, string> = {
        instagram: 'instagram',
        blog: 'naverblog',
        youtube: 'youtube',
      };
      return channelFilter.some(ch => {
        const key = channelToHandleKey[ch] || ch;
        return !!handles[key]?.account;
      });
    });
  }
  if (sortBy === 'recent') filtered.sort((a: any, b: any) => (b.appliedAt || b.createdAt || '').localeCompare(a.appliedAt || a.createdAt || ''));
  else if (sortBy === 'oldest') filtered.sort((a: any, b: any) => (a.appliedAt || a.createdAt || '').localeCompare(b.appliedAt || b.createdAt || ''));

  const toggleChannel = (ch: string) => {
    setChannelFilter(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);
  };

  const tabs: { key: TabType; label: string }[] = [
    { key: 'applicants', label: t('applicants.tabs.applicants') },
    { key: 'progress', label: t('applicants.tabs.progress') },
    { key: 'settlement', label: t('applicants.tabs.settlement') },
  ];

  const krwFormatter = new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' });

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Back */}
      <button onClick={() => navigate('/campaigns')} className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeftIcon className="w-4 h-4 mr-1" /> {t('applicants.backToList')}
      </button>

      {/* Campaign Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex gap-6">
          {/* Representative Image */}
          <div className="w-40 h-28 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
            {campaign.representativeImages?.[0]?.fileUrl ? (
              <img src={campaign.representativeImages[0].fileUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">No Image</div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            {/* Title & Channels */}
            <h1 className="text-xl font-bold text-gray-900 mb-2">{campaign.campaignName || t('applicants.noTitle')}</h1>
            {channels.length > 0 && (
              <div className="flex items-center gap-2 mb-3">
                {channels.map((ch: string) => (
                  <div key={ch} className="flex items-center gap-1">
                    <ChannelIcon channel={ch} className="w-4 h-4" />
                    <span className="text-xs text-gray-500">{ch === 'instagram' ? t('applicants.channels.instagram') : ch === 'blog' ? t('applicants.channels.blog') : ch === 'youtube' ? t('applicants.channels.youtube') : ch}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Schedule Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-600">
              <div>
                <span className="text-gray-400 block">{t('applicants.schedule.recruitmentPeriod')}</span>
                <span>{campaign.recruitmentPeriod?.startDate?.slice(0, 10) || '-'} ~ {campaign.recruitmentPeriod?.endDate?.slice(0, 10) || '-'}</span>
              </div>
              <div>
                <span className="text-gray-400 block">{t('applicants.schedule.announcementDate')}</span>
                <span>{(campaign as any).alwaysOpening ? t('applicants.schedule.alwaysOpening') : `${campaign.announcementStartDate?.slice(0, 10) || '-'}${campaign.announcementEndDate ? ` ~ ${campaign.announcementEndDate.slice(0, 10)}` : ''}`}</span>
              </div>
              <div>
                <span className="text-gray-400 block">{t('applicants.schedule.visitPeriod')}</span>
                <span>{campaign.visitStartDate?.slice(0, 10) || '-'}{campaign.visitEndDate ? ` ~ ${campaign.visitEndDate.slice(0, 10)}` : ''}</span>
              </div>
              <div>
                <span className="text-gray-400 block">{t('applicants.schedule.contentUploadPeriod')}</span>
                <span>{campaign.uploadDeadlineDays ? t('applicants.schedule.afterVisitDays', { days: campaign.uploadDeadlineDays }) : '-'}</span>
              </div>
            </div>

            {/* Applicant / Recruit Count */}
            <div className="mt-3 text-sm">
              <span className="text-blue-600 font-semibold">{t('applicants.applicantCount', { count: applicantCount })}</span>
              <span className="text-gray-400 mx-2">/</span>
              <span className="text-gray-600">{t('applicants.recruitCount', { count: campaign.numberOfRecruits || 0 })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'applicants' && (
        <div>
          {/* Stats Filter Tabs */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <button onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${statusFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {t('applicants.progress.all')} {t('applicants.stats.count', { count: applicantCount })}
            </button>
            <button onClick={() => setStatusFilter('applied')}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${statusFilter === 'applied' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>
              {t('applicants.stats.applicants')} {t('applicants.stats.count', { count: applications.filter(a => a.status === 'applied').length })}
            </button>
            <button onClick={() => setStatusFilter('shortlisted')}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${statusFilter === 'shortlisted' ? 'bg-blue-600 text-white' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'}`}>
              {t('applicants.stats.firstRound')} {t('applicants.stats.count', { count: firstRoundCount })}
            </button>
            <button onClick={() => setStatusFilter('accepted')}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${statusFilter === 'accepted' ? 'bg-blue-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
              {t('applicants.stats.finalTarget')} {t('applicants.stats.count', { count: finalCount })}
              <span className="text-gray-400 ml-1">({t('applicants.stats.recruitTarget', { count: campaign.numberOfRecruits || 0 })})</span>
            </button>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{t('applicants.filter.connectedChannel')}</span>
              {['instagram', 'blog', 'youtube'].map(ch => (
                <button key={ch} onClick={() => toggleChannel(ch)}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full border transition-colors ${
                    channelFilter.includes(ch) ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}>
                  <ChannelIcon channel={ch} className="w-3 h-3" />
                  {t(`applicants.channels.${ch}`)}
                </button>
              ))}
            </div>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="px-3 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="recent">{t('applicants.filter.sortRecent')}</option>
              <option value="oldest">{t('applicants.filter.sortOldest')}</option>
              <option value="followers">{t('applicants.filter.sortFollowers')}</option>
            </select>
          </div>

          {/* Applicant List */}
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">{t('applicants.empty')}</div>
          ) : (
            <div className="space-y-3">
              {filtered.map((app: any) => {
                const handles = app.socialMediaHandles || {};
                const snsRows = [
                  { key: 'instagram', icon: 'instagram', account: handles.instagram?.account, followers: handles.instagram?.followerCount, urlPrefix: 'https://www.instagram.com/' },
                  { key: 'naverblog', icon: 'blog', account: handles.naverblog?.account, followers: handles.naverblog?.followerCount, urlPrefix: 'https://blog.naver.com/' },
                  { key: 'youtube', icon: 'youtube', account: handles.youtube?.account, followers: handles.youtube?.followerCount, urlPrefix: 'https://www.youtube.com/@' },
                ].filter(r => r.account);

                return (
                <div key={app.matchId} className="bg-white rounded-lg border border-gray-200 p-4">
                  {/* Row 1: Profile image + Nickname + 상세보기 + Action button */}
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                      {app.profileImage ? (
                        <img src={app.profileImage} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm font-bold">
                          {(app.nickname || app.influencerName || '?')[0]?.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span className="text-sm font-bold text-gray-900">{app.nickname || app.influencerName || `${t('applicants.influencer')} ${app.influencerId?.slice(0, 8)}`}</span>
                    <button onClick={() => openProfileModal(app)}
                      className="text-xs text-blue-600 hover:underline">{t('applicants.viewDetail')}</button>
                    <div className="ml-auto flex items-center gap-2">
                      {app.status === 'applied' && (
                        <button
                          onClick={async () => {
                            try { await graphqlApi.shortlistMatch(app.matchId); fetchData(); } catch (err) { console.error(err); }
                          }}
                          className="px-4 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                          {t('applicants.shortlistBtn')}
                        </button>
                      )}
                      {app.status === 'shortlisted' && (
                        <button
                          onClick={async () => {
                            try { await graphqlApi.requestMatch(app.matchId); fetchData(); } catch (err) { console.error(err); }
                          }}
                          className="px-4 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors">
                          {t('applicants.requestApproval', '승인요청')}
                        </button>
                      )}
                      {app.status === 'requested' && (
                        <span className="px-4 py-1.5 text-xs font-medium rounded-lg bg-yellow-100 text-yellow-700">
                          {t('applicants.requestSent')}
                        </span>
                      )}
                      {app.status === 'accepted' && (
                        <span className="px-4 py-1.5 text-xs font-medium rounded-lg bg-green-100 text-green-700">
                          {t('applicants.accepted')}
                        </span>
                      )}
                      {app.status === 'completed' && (
                        <span className="px-4 py-1.5 text-xs font-medium rounded-lg bg-blue-100 text-blue-700">
                          {t('applicants.completed', '완료')}
                        </span>
                      )}
                      {app.status === 'canceled' && (
                        <span className="px-4 py-1.5 text-xs font-medium rounded-lg bg-red-100 text-red-700">
                          {t('applicants.canceled', '취소')}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Row 2: SNS channels with account ID/link + follower count */}
                  {snsRows.length > 0 && (
                    <div className="ml-13 space-y-1 pl-[52px]">
                      {snsRows.map(row => {
                        const displayId = row.account!.startsWith('http') ? row.account!.replace(row.urlPrefix, '') : row.account!;
                        const href = row.account!.startsWith('http') ? row.account! : `${row.urlPrefix}${row.account}`;
                        return (
                          <div key={row.key} className="flex items-center gap-2 text-xs text-gray-600">
                            <ChannelIcon channel={row.icon} className="w-4 h-4 flex-shrink-0" />
                            <a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 hover:underline truncate max-w-[200px]">{displayId}</a>
                            <span className="text-gray-400">|</span>
                            <span className="font-medium text-gray-700">{row.followers?.toLocaleString() || '0'}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Applied date + reward */}
                  <div className="mt-2 pl-[52px] flex items-center gap-3">
                    <span className="text-[10px] text-gray-400">{t('applicants.appliedAt')} {(app.appliedAt || app.createdAt)?.slice(0, 10)}</span>
                    <span className="text-[10px] text-gray-400">·</span>
                    {typeof app.rewardAmount === 'number' ? (
                      <span className="text-xs font-semibold text-gray-900">
                        {krwFormatter.format(app.rewardAmount)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {t('profile:myCampaigns.rewardUnavailable', 'Reward details unavailable')}
                      </span>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'progress' && (() => {
        const progressMatches = applications.filter(a =>
          ['shortlisted', 'requested', 'accepted', 'completed', 'canceled', 'rejected', 'expired'].includes(a.status)
        );
        const collaboratorCount = progressMatches.filter(a => ['shortlisted', 'requested', 'accepted', 'completed'].includes(a.status)).length;
        const pc = campaign.publishingConditions || {};

        // Calculate total content target based on each collaborator's selectedMissions from match DB
        const activeCollaborators = progressMatches.filter(a => ['shortlisted', 'requested', 'accepted', 'completed'].includes(a.status));
        const totalContentTarget = activeCollaborators.reduce((total: number, match: any) => {
          const missions: string[] = match.selectedMissions || [];
          // Sum conditions only for missions this collaborator selected
          let matchTotal = 0;
          for (const mission of missions) {
            const channelConditions = pc[mission]?.conditions || [];
            matchTotal += channelConditions.reduce((sum: number, c: any) => sum + (c.quantity || 0), 0);
          }
          // If no selectedMissions recorded, fallback to all campaign conditions
          if (missions.length === 0) {
            const allConditions = [
              ...(pc.instagram?.conditions || []),
              ...(pc.blog?.conditions || []),
              ...(pc.youtube?.conditions || []),
            ];
            matchTotal = allConditions.reduce((sum: number, c: any) => sum + (c.quantity || 0), 0);
          }
          return total + matchTotal;
        }, 0);
        const submittedContent = 0;
        const pendingContent = totalContentTarget - submittedContent;
        const totalReward = (campaign.rewardAmount || 0) * collaboratorCount;
        const matchingFee = Math.round(totalReward * 0.1);

        const getProgressFiltered = () => {
          switch (progressFilter) {
            case 'in_progress': return progressMatches.filter(a => ['shortlisted', 'requested', 'accepted'].includes(a.status));
            case 'settlement_needed': return progressMatches.filter(a => a.status === 'completed');
            case 'done': return progressMatches.filter(a => a.status === 'completed');
            case 'canceled': return progressMatches.filter(a => ['canceled', 'rejected', 'expired'].includes(a.status));
            default: return progressMatches;
          }
        };
        const progressFiltered = getProgressFiltered();

        const statusLabel = (s: string) => t(`applicants.progress.statusLabel.${s}`, s);

        const statusColor = (s: string) => {
          switch (s) {
            case 'shortlisted': return 'text-indigo-600 bg-indigo-50';
            case 'requested': return 'text-yellow-600 bg-yellow-50';
            case 'accepted': return 'text-blue-600 bg-blue-50';
            case 'completed': return 'text-green-600 bg-green-50';
            case 'canceled': case 'rejected': case 'expired': return 'text-red-600 bg-red-50';
            default: return 'text-gray-600 bg-gray-50';
          }
        };

        return (
          <div>
            {/* Summary Cards */}
            <h3 className="text-base font-semibold text-gray-900 mb-3">{t('applicants.progress.summary')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <span className="text-xs text-gray-500">{t('applicants.progress.collaboratorCount')}</span>
                <p className="text-xl font-bold text-blue-600 mt-1">{t('applicants.stats.count', { count: collaboratorCount })}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <span className="text-xs text-gray-500">{t('applicants.progress.contentStatus')}</span>
                <div className="flex items-baseline gap-3 mt-1">
                  <span className="text-sm">{t('applicants.progress.target')} <span className="font-bold text-gray-900">{totalContentTarget}{t('applicants.items')}</span></span>
                  <span className="text-sm">{t('applicants.progress.submitted')} <span className="font-bold text-green-600">{submittedContent}{t('applicants.items')}</span></span>
                  <span className="text-sm">{t('applicants.progress.notSubmitted')} <span className="font-bold text-red-600">{pendingContent}{t('applicants.items')}</span></span>
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <span className="text-xs text-gray-500">{t('applicants.progress.cost')}</span>
                <div className="flex items-baseline gap-3 mt-1">
                  <span className="text-sm">{t('applicants.progress.contentReward')} <span className="font-bold text-gray-900">${totalReward.toLocaleString()}</span></span>
                  <span className="text-sm">{t('applicants.progress.matchingFee')} <span className="font-bold text-gray-900">${matchingFee.toLocaleString()}</span></span>
                </div>
              </div>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-gray-500">{t('applicants.progress.statusFilter')}</span>
              {([
                { key: 'all' as const, label: t('applicants.progress.all') },
                { key: 'in_progress' as const, label: t('applicants.progress.inProgress') },
                { key: 'settlement_needed' as const, label: t('applicants.progress.settlementNeeded') },
                { key: 'done' as const, label: t('applicants.progress.done') },
                { key: 'canceled' as const, label: t('applicants.progress.canceled') },
              ]).map(f => (
                <button key={f.key} onClick={() => setProgressFilter(f.key)}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    progressFilter === f.key ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="text-xs text-gray-500 mb-2">{t('applicants.progress.totalCount', { count: progressFiltered.length })}</div>
            {progressFiltered.length === 0 ? (
              <div className="text-center py-12 text-gray-400">{t('applicants.progress.emptyProgress')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                      <th className="py-2 px-3 font-medium">{t('applicants.progress.table.status')}</th>
                      <th className="py-2 px-3 font-medium">{t('applicants.progress.table.influencer')}</th>
                      <th className="py-2 px-3 font-medium">{t('applicants.progress.table.visitConfirm')}</th>
                      <th className="py-2 px-3 font-medium">{t('applicants.progress.table.contentStatus')}</th>
                      <th className="py-2 px-3 font-medium">{t('applicants.progress.table.reward', '리워드')}</th>
                      <th className="py-2 px-3 font-medium">{t('applicants.progress.table.matchingFee')}</th>
                      <th className="py-2 px-3 font-medium">{t('applicants.progress.table.review')}</th>
                      <th className="py-2 px-3 font-medium">{t('applicants.progress.table.cancelProgress')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {progressFiltered.map((app) => {
                      const perInfluencerFee = campaign.rewardAmount || 0;
                      const fee = Math.round(perInfluencerFee * 0.1);
                      return (
                        <tr key={app.matchId} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColor(app.status)}`}>
                              {statusLabel(app.status)}
                            </span>
                          </td>
                          <td className="py-3 px-3 font-medium text-gray-900">
                            {app.influencerName || `${t('applicants.influencer')} ${app.influencerId?.slice(0, 8)}`}
                          </td>
                          <td className="py-3 px-3">
                            {(app as any).visitStatus === 'completed' ? (
                              <span className="px-3 py-1 text-xs font-medium text-green-700 bg-green-50 rounded-lg">
                                {t('applicants.progress.visitCompleted', '방문완료')}
                              </span>
                            ) : app.status === 'accepted' ? (
                              <button
                                onClick={async () => {
                                  if (!window.confirm(t('applicants.progress.visitCompleteConfirm', '방문완료 처리하시겠습니까?'))) return;
                                  try {
                                    await graphqlApi.updateMatch(app.matchId, { visitStatus: 'completed' } as any);
                                    fetchData();
                                  } catch (err) { console.error(err); }
                                }}
                                className="px-3 py-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100">
                                {t('applicants.progress.notVisited', '미방문')}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-3">
                              <button onClick={() => openContentReviewModal(app)} className="px-3 py-1 text-xs font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50">{t('applicants.progress.viewInspect', '조회/검수')}</button>
                          </td>
                          <td className="py-3 px-3">
                            {typeof app.rewardAmount === 'number' ? (
                              <span className="text-gray-900 font-medium">{krwFormatter.format(app.rewardAmount)}</span>
                            ) : (
                              <span className="text-gray-400">{t('profile:myCampaigns.rewardUnavailable', 'Reward details unavailable')}</span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-gray-700">${fee.toLocaleString()}</td>
                          <td className="py-3 px-3">
                            <button onClick={() => { setReviewModalApp(app); setReviewGrades({ communicationSpeed: 0, visitPunctuality: 0, collaborationAttitude: 0, submissionPunctuality: 0, contentQuality: 0 }); setReviewComment(''); }}
                              className="px-3 py-1 text-xs font-medium text-yellow-700 border border-yellow-300 rounded-lg hover:bg-yellow-50">
                              {t('applicants.progress.reviewBtn')}
                            </button>
                          </td>
                          <td className="py-3 px-3">
                            {['shortlisted', 'requested', 'accepted'].includes(app.status) ? (
                              <button
                                onClick={() => { setCancelModalApp(app); setCancelReason(''); }}
                                className="px-3 py-1 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50">
                                {t('applicants.progress.cancelBtn')}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {activeTab === 'settlement' && (
        <div className="text-center py-12 text-gray-400">{t('applicants.settlementComingSoon')}</div>
      )}

      {/* Influencer Profile Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeProfileModal}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
            {modalLoading ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : (
              <>
                {/* Header: Profile Image + Nickname */}
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                    {selectedApp.profileImage ? (
                      <img src={selectedApp.profileImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-lg font-bold">
                        {(selectedApp.nickname || selectedApp.influencerName || '?')[0]?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <span className="text-lg font-bold text-gray-900">{selectedApp.nickname || selectedApp.influencerName}</span>
                </div>
                <hr className="border-gray-200" />

                {/* 연동채널 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">연동채널</h3>
                  <div className="space-y-1.5">
                    {[
                      { key: 'instagram', icon: 'instagram', prefix: 'https://www.instagram.com/' },
                      { key: 'naverblog', icon: 'blog', prefix: 'https://blog.naver.com/' },
                      { key: 'youtube', icon: 'youtube', prefix: 'https://www.youtube.com/@' },
                    ].map(ch => {
                      const h = (selectedApp.socialMediaHandles || {} as any)[ch.key];
                      if (!h?.account) return null;
                      const displayId = h.account.startsWith('http') ? h.account.replace(ch.prefix, '') : h.account;
                      const href = h.account.startsWith('http') ? h.account : `${ch.prefix}${h.account}`;
                      return (
                        <div key={ch.key} className="flex items-center gap-2 text-sm text-gray-600">
                          <ChannelIcon channel={ch.icon} className="w-4 h-4" />
                          <a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 hover:underline truncate max-w-[200px]">{displayId}</a>
                          <span className="text-gray-400">|</span>
                          <span className="font-medium text-gray-700">{h.followerCount?.toLocaleString() || '0'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <hr className="border-gray-200" />

                {/* 신청정보 */}
                {selectedMatchDetail?.selectedMissions?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">신청정보</h3>
                    <div className="space-y-2">
                      {selectedMatchDetail.selectedMissions.map((mission: string) => {
                        const pc = (campaign as any)?.publishingConditions;
                        const channelData = pc?.[mission];
                        const label = mission === 'instagram' ? '인스타그램' : mission === 'blog' ? '블로그' : mission === 'youtube' ? '유튜브' : mission;
                        return (
                          <div key={mission} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center gap-1.5 mb-1">
                              <ChannelIcon channel={mission === 'blog' ? 'blog' : mission} className="w-4 h-4" />
                              <span className="text-sm font-medium text-gray-800">{label}</span>
                            </div>
                            {channelData?.conditions?.map((cond: any, idx: number) => (
                              <div key={idx} className="flex gap-4 text-xs text-gray-600 ml-5">
                                <span>항목: {cond.item}</span>
                                <span>수량: {cond.quantity}건</span>
                              </div>
                            ))}
                            {(channelData?.collaboratorId || channelData?.insertLink) && (
                              <div className="text-xs text-gray-500 ml-5 mt-1">
                                {mission === 'instagram' ? '공동작업자' : '콘텐츠 내 삽입링크'}: {channelData.collaboratorId || channelData.insertLink || '-'}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {selectedMatchDetail?.selectedMissions?.length > 0 && <hr className="border-gray-200" />}

                {/* 방문정보 */}
                {selectedMatchDetail?.visitInfo && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">방문정보</h3>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>
                        <span className="text-gray-500">방문 가능 일정: </span>
                        <span>{selectedMatchDetail.visitInfo.startDate || '-'} ~ {selectedMatchDetail.visitInfo.endDate || '-'}</span>
                      </div>
                      <div className="flex gap-4">
                        <span>성인 <span className="font-medium">{selectedMatchDetail.visitInfo.adults || 0}</span>명</span>
                        <span>아동 <span className="font-medium">{selectedMatchDetail.visitInfo.children || 0}</span>명</span>
                        <span>유아 <span className="font-medium">{selectedMatchDetail.visitInfo.infants || 0}</span>명</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Close button */}
                <div className="pt-2">
                  <button onClick={closeProfileModal} className="w-full py-2.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50">
                    닫기
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Cancel Progress Modal */}
      {cancelModalApp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCancelModalApp(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900">{t('applicants.cancelModal.title', '진행취소')}</h2>
            <div className="text-sm text-gray-600">
              {t('applicants.cancelModal.influencer', '인플루언서')}: <span className="font-medium text-gray-900">{cancelModalApp.nickname || cancelModalApp.influencerName || '-'}</span>
            </div>
            <hr className="border-gray-200" />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('applicants.cancelModal.reasonLabel', '진행 취소 사유')}
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                rows={3}
                required
                aria-required="true"
                placeholder={t('applicants.cancelModal.reasonPlaceholder', '진행 취소 사유를 입력하세요')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              />
              {cancelReason.trim().length === 0 && (
                <p className="mt-1 text-xs text-red-500">
                  {t('applicants.cancelModal.reasonRequired', '진행 취소 사유는 필수입니다.')}
                </p>
              )}
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 space-y-2">
              <p>{t('applicants.cancelModal.warning1', '진행 중인 인플루언서와 사전 협의 없이 진행을 취소할 경우, 서비스 이용이 제한될 수 있습니다.')}</p>
              <p>{t('applicants.cancelModal.warning2', "아래 '진행 취소하기' 선택 시 크리에이터의 캠페인 참여가 취소되고 알림이 발송되며, 진행 취소로 인해 미사용된 금액은 캠페인 종료 후 환불됩니다.")}</p>
              <p>{t('applicants.cancelModal.warning3', '결제 건은 카드 승인취소, 계좌 결제 건은 등록된 환불 계좌로 환불됩니다.')}</p>
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li>{t('applicants.cancelModal.warning4', '카드 결제 후 1년을 경과한 경우, 환불 계좌로 환불 처리되나 환불 수수료(취소 금액의 3%)가 발생할 수 있습니다.')}</li>
                <li>{t('applicants.cancelModal.warning5', '환불 계좌는 등록된 사업자 유형에 따라 사업자 대표자 명의 또는 법인명의 계좌만 등록할 수 있습니다.')}</li>
              </ul>
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setCancelModalApp(null)} className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50">
                {t('common:cancel', '취소')}
              </button>
              <button
                disabled={cancelSubmitting || cancelReason.trim().length === 0}
                onClick={async () => {
                  const trimmedReason = cancelReason.trim();
                  if (trimmedReason.length === 0) {
                    toast.error(t('applicants.cancelModal.reasonRequired', '진행 취소 사유는 필수입니다.'));
                    return;
                  }
                  setCancelSubmitting(true);
                  try {
                    const res = await graphqlApi.cancelMatch(cancelModalApp.matchId, trimmedReason);
                    if (res.success) {
                      fetchData();
                      setCancelModalApp(null);
                    } else {
                      // Backend guard — see handleCancelMatch in backend.
                      // The message is matched by substring so future wording
                      // tweaks still land on the right toast.
                      const msg = (res.error || '').toLowerCase();
                      if (msg.includes('already submitted content')) {
                        toast.error(t('applicants.cancelModal.errorContentSubmitted', '이미 인플루언서가 콘텐츠를 제출하여 진행을 취소할 수 없습니다.'));
                      } else if (msg.includes('cancellation reason is required')) {
                        toast.error(t('applicants.cancelModal.reasonRequired', '진행 취소 사유는 필수입니다.'));
                      } else {
                        toast.error(t('applicants.cancelModal.errorGeneric', '진행 취소에 실패했습니다. 다시 시도해 주세요.'));
                      }
                    }
                  } catch (err) {
                    console.error(err);
                    toast.error(t('applicants.cancelModal.errorGeneric', '진행 취소에 실패했습니다. 다시 시도해 주세요.'));
                  } finally { setCancelSubmitting(false); }
                }}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {cancelSubmitting ? '...' : t('applicants.cancelModal.confirmBtn', '진행 취소하기')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Influencer Review Modal */}
      {reviewModalApp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setReviewModalApp(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900">{t('applicants.review.title', '인플루언서 리뷰')}</h2>
            <div className="text-sm text-gray-600">
              {t('applicants.review.influencer', '인플루언서')}: <span className="font-medium text-gray-900">{reviewModalApp.nickname || reviewModalApp.influencerName || '-'}</span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              {t('applicants.review.notice', '본 리뷰는 인플루언서에게 공개되지 않으며, 더욱 정교한 매칭 서비스를 제공하기 위한 시스템 고도화와 서비스 품질 향상을 위해서만 안전하게 활용됩니다. 소중한 의견을 들려주셔서 진심으로 감사합니다.')}
            </p>
            <hr className="border-gray-200" />
            <h3 className="text-sm font-semibold text-gray-800">{t('applicants.review.question', '인플루언서와의 협업이 어떠셨나요?')}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500">
                    <th className="py-1 px-1 text-left font-medium w-28"></th>
                    <th className="py-1 px-1 text-center font-medium">{t('applicants.review.veryGood', '매우만족')}</th>
                    <th className="py-1 px-1 text-center font-medium">{t('applicants.review.good', '만족')}</th>
                    <th className="py-1 px-1 text-center font-medium">{t('applicants.review.normal', '보통')}</th>
                    <th className="py-1 px-1 text-center font-medium">{t('applicants.review.bad', '불만족')}</th>
                    <th className="py-1 px-1 text-center font-medium">{t('applicants.review.veryBad', '매우불만족')}</th>
                  </tr>
                </thead>
                <tbody>
                  {([
                    { key: 'communicationSpeed' as const, label: t('applicants.review.communicationSpeed', '소통응답속도') },
                    { key: 'visitPunctuality' as const, label: t('applicants.review.visitPunctuality', '방문약속 엄수') },
                    { key: 'collaborationAttitude' as const, label: t('applicants.review.collaborationAttitude', '협업태도') },
                    { key: 'submissionPunctuality' as const, label: t('applicants.review.submissionPunctuality', '제출기한 엄수') },
                    { key: 'contentQuality' as const, label: t('applicants.review.contentQuality', '콘텐츠 퀄리티') },
                  ]).map(row => (
                    <tr key={row.key} className="border-t border-gray-100">
                      <td className="py-2 px-1 text-gray-700 font-medium">{row.label}</td>
                      {[5, 4, 3, 2, 1].map(val => (
                        <td key={val} className="py-2 px-1 text-center">
                          <input type="radio" name={row.key} checked={reviewGrades[row.key] === val}
                            onChange={() => setReviewGrades(prev => ({ ...prev, [row.key]: val }))}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('applicants.review.comment', '기타 의견')}</label>
              <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)} rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>
            <button
              disabled={reviewSubmitting || Object.values(reviewGrades).some(v => v === 0)}
              onClick={async () => {
                setReviewSubmitting(true);
                try {
                  const res = await graphqlApi.createInfluencerReview({
                    matchId: reviewModalApp.matchId,
                    campaignId: reviewModalApp.campaignId,
                    influencerId: reviewModalApp.influencerId,
                    ...reviewGrades,
                    comment: reviewComment.trim() || undefined,
                  });
                  if (res.success) {
                    setReviewModalApp(null);
                    toast.success(t('applicants.review.submitted', '리뷰가 제출되었습니다.'));
                  } else {
                    toast.error(res.error || 'Failed');
                  }
                } catch (err: any) { toast.error(err.message); }
                finally { setReviewSubmitting(false); }
              }}
              className="w-full py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {reviewSubmitting ? '...' : t('applicants.review.submitBtn', '제출하기')}
            </button>
          </div>
        </div>
      )}

      {/* Content Review Modal */}
      {contentReviewApp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeContentReviewModal}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
            {contentReviewLoading ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : (() => {
              const pc = (campaign as any)?.publishingConditions || {};
              const missions: string[] = contentReviewMatch?.selectedMissions || [];
              const subs: any[] = contentReviewMatch?.contentSubmissions || [];
              // Build flat rows from selected missions
              let seq = 0;
              const rows: { seq: number; channel: string; channelLabel: string; item: string; rewardAmount: number; startDate: string; endDate: string; contentLink?: string; submittedAt?: string; subStatus?: string }[] = [];
              for (const mission of missions) {
                const channelData = pc[mission];
                const conditions = channelData?.conditions || [];
                const channelLabel = mission === 'instagram' ? '인스타그램' : mission === 'blog' ? '블로그' : mission === 'youtube' ? '유튜브' : mission;
                for (const cond of conditions) {
                  seq++;
                  const sub = subs.find((s: any) => s.seq === seq);
                  rows.push({
                    seq,
                    channel: mission,
                    channelLabel,
                    item: cond.item === 'reels' ? '릴스' : cond.item === 'story' ? '스토리' : cond.item === 'post' || cond.item === 'feed' ? '포스트/피드' : cond.item === 'blogPost' ? '블로그 포스트' : cond.item === 'video' ? '영상' : cond.item === 'shorts' ? '쇼츠' : cond.item,
                    rewardAmount: 0,
                    startDate: campaign?.visitStartDate?.slice(0, 10) || '-',
                    endDate: campaign?.visitEndDate?.slice(0, 10) || '-',
                    contentLink: sub?.contentLink,
                    submittedAt: sub?.submittedAt,
                    subStatus: sub?.status,
                  });
                }
              }
              // Recalculate reward per row evenly
              const totalReward = campaign?.rewardAmount || 0;
              const perRow = rows.length > 0 ? Math.round(totalReward / rows.length) : 0;
              rows.forEach(r => { r.rewardAmount = perRow; });

              const totalCount = rows.length;
              const submittedCount = rows.filter(r => !!r.contentLink).length;
              const notSubmittedCount = totalCount - submittedCount;

              return (
                <>
                  <h2 className="text-lg font-bold text-gray-900">{t('applicants.contentReview.title', '콘텐츠 현황')}</h2>
                  <div className="text-sm text-gray-600">
                    {t('applicants.contentReview.influencer', '인플루언서')}: <span className="font-medium text-gray-900">{contentReviewApp.nickname || contentReviewApp.influencerName || '-'}</span>
                  </div>
                  <hr className="border-gray-200" />

                  {/* Summary */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">{t('applicants.contentReview.summary', '제출 요약')}</h3>
                    <div className="flex flex-wrap gap-4 p-3 bg-gray-50 rounded-lg text-sm">
                      <div>{t('applicants.contentReview.totalItems', '전체')} <span className="font-bold text-gray-900">{totalCount}{t('applicants.items', '건')}</span></div>
                      <div>{t('applicants.contentReview.submitted', '제출')} <span className="font-bold text-green-600">{submittedCount}{t('applicants.items', '건')}</span></div>
                      <div>{t('applicants.contentReview.notSubmitted', '미제출')} <span className="font-bold text-red-600">{notSubmittedCount}{t('applicants.items', '건')}</span></div>
                      <div>{t('applicants.contentReview.contentReward', '콘텐츠 리워드')} <span className="font-bold text-blue-600">${totalReward.toLocaleString()}</span></div>
                    </div>
                  </div>

                  {/* Submission Table */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">{t('applicants.contentReview.submittedContent', '제출 콘텐츠')}</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 text-gray-500">
                            <th className="px-2 py-2 text-left font-medium">{t('applicants.contentReview.col.seq', '순번')}</th>
                            <th className="px-2 py-2 text-left font-medium">{t('applicants.contentReview.col.channel', '채널')}</th>
                            <th className="px-2 py-2 text-left font-medium">{t('applicants.contentReview.col.item', '항목')}</th>
                            <th className="px-2 py-2 text-left font-medium">{t('applicants.contentReview.col.period', '제출기간')}</th>
                            <th className="px-2 py-2 text-left font-medium">{t('applicants.contentReview.col.content', '제출 콘텐츠')}</th>
                            <th className="px-2 py-2 text-right font-medium">{t('applicants.contentReview.col.reward', '리워드')}</th>
                            <th className="px-2 py-2 text-center font-medium">{t('applicants.contentReview.col.confirm', '진행완료 확정')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.length === 0 ? (
                            <tr><td colSpan={7} className="px-2 py-6 text-center text-gray-400">{t('applicants.contentReview.noItems', '콘텐츠 항목이 없습니다')}</td></tr>
                          ) : rows.map((row) => (
                            <tr key={row.seq} className="border-b border-gray-100">
                              <td className="px-2 py-2 text-gray-700">{row.seq}</td>
                              <td className="px-2 py-2">
                                <div className="flex items-center gap-1">
                                  <ChannelIcon channel={row.channel === 'blog' ? 'blog' : row.channel} className="w-3.5 h-3.5" />
                                  <span className="text-gray-700">{row.channelLabel}</span>
                                </div>
                              </td>
                              <td className="px-2 py-2 text-gray-700">{row.item}</td>
                              <td className="px-2 py-2 text-gray-600">{row.startDate} ~ {row.endDate}</td>
                              <td className="px-2 py-2">
                                {row.contentLink ? (
                                  <a href={row.contentLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block max-w-[160px]">{row.contentLink}</a>
                                ) : (
                                  <span className="text-gray-400">{t('applicants.contentReview.notYet', '미제출')}</span>
                                )}
                              </td>
                              <td className="px-2 py-2 text-right text-gray-700">${row.rewardAmount.toLocaleString()}</td>
                              <td className="px-2 py-2 text-center">
                                {row.subStatus === 'confirmed' ? (
                                  <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-green-100 text-green-600">{t('applicants.contentReview.confirmed', '확정')}</span>
                                ) : row.contentLink ? (
                                  <button
                                    onClick={async () => {
                                      try {
                                        // Update this submission's status to confirmed, strip __typename
                                        const updatedSubs = (contentReviewMatch?.contentSubmissions || []).map((s: any) => {
                                          const { __typename, ...clean } = s;
                                          return s.seq === row.seq ? { ...clean, status: 'confirmed' } : clean;
                                        });
                                        // Optimistic update — immediately show confirmed state
                                        setContentReviewMatch((prev: any) => prev ? { ...prev, contentSubmissions: updatedSubs } : prev);
                                        await graphqlApi.updateMatch(contentReviewApp!.matchId, { contentSubmissions: updatedSubs } as any);
                                        toast.success(t('applicants.contentReview.confirmSuccess', '진행완료 확정되었습니다.'));
                                      } catch (err: any) {
                                        // Revert on error
                                        const res = await graphqlApi.getMatch(contentReviewApp!.matchId);
                                        if (res.success && res.data) setContentReviewMatch(res.data);
                                        toast.error(err.message);
                                      }
                                    }}
                                    className="px-2 py-0.5 text-[10px] font-medium text-blue-600 border border-blue-300 rounded-full hover:bg-blue-50">
                                    {t('applicants.contentReview.confirmBtn', '확정하기')}
                                  </button>
                                ) : (
                                  <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-500">{t('applicants.contentReview.pending', '대기')}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Close */}
                  <div className="pt-2">
                    <button onClick={closeContentReviewModal} className="w-full py-2.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50">
                      {t('common:confirm', '닫기')}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignApplicantsPage;
